"use client";

import { blogStyles } from "@/styles/blogContent";
import { SLContainer } from "@/components/layout/SLContainer";
import { SLHtmlFromLangFile } from "@/components/sections/home/SLHtmlFromLangFile";
import { ExpertiseCityStringSet } from "@/lib/strings/expertiseCityStrings";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { important } from "@nvon/react-toolbox";

/**
 * Insolvenzberichte accordion section for location-specific pages.
 */
export const SLExpertiseInsolvenzberichte = ({
  expertiseCity,
}: {
  expertiseCity: ExpertiseCityStringSet;
}) => {
  const theme = useTheme();
  const section = expertiseCity.insolvenzenSection;
  if (!section) return null;

  return (
    <SLContainer hasPaddingTop={false} width="md" sx={blogStyles(theme)}>
      <Typography mb={4} variant="h2">
        {section.title}
      </Typography>
      <Typography mb={4} variant="body1">
        <SLHtmlFromLangFile htmlString={section.description} />
      </Typography>

      <Stack>
        {section.insolvenzberichte.map((bericht, index) => (
          <Accordion
            key={bericht.month}
            defaultExpanded={index === 0}
            sx={{
              mb: 1,
              backgroundColor: "transparent",
              boxShadow: "none",
              border: `1px solid ${theme.palette.success.main}`,
              p: 1,
            }}
          >
            <AccordionSummary>
              <Typography
                variant="h3"
                sx={{
                  fontSize: important(theme.typography.h6.fontSize ?? 0),
                  color: theme.palette.success.main,
                }}
              >
                {new Date(bericht.month).toLocaleDateString("de-DE", {
                  month: "long",
                  year: "numeric",
                })}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {bericht.cases.map((insolvenzcase) => (
                  <ListItem key={insolvenzcase.position}>
                    <ListItemAvatar sx={{ alignSelf: "flex-start", mt: 1 }}>
                      <Avatar
                        sx={{ backgroundColor: theme.palette.primary.dark }}
                      >
                        {insolvenzcase.position}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      slotProps={{
                        secondary: {
                          sx: { color: theme.palette.primary.dark },
                        },
                      }}
                      primary={
                        <Stack>
                          <Typography
                            variant="h4"
                            sx={{ color: theme.palette.primary.dark }}
                          >
                            {insolvenzcase.firma}
                          </Typography>
                          <Box mt={2}>
                            <Chip
                              color="warning"
                              size="small"
                              label={insolvenzcase.branche}
                            />
                          </Box>
                        </Stack>
                      }
                      secondary={
                        <SLHtmlFromLangFile
                          htmlString={insolvenzcase.description}
                        />
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </SLContainer>
  );
};
